<template>
  <div>
    <HeroSection />
    
    <!-- Features Section -->
    <section class="py-20 bg-black-800 overflow-hidden">
      <div class="container mx-auto px-6">
        <h2 class="section-title transform transition-all duration-1000" 
            :class="{'translate-y-0 opacity-100': featuresVisible, 'translate-y-10 opacity-0': !featuresVisible}"
            ref="featuresTitle">
          Why Choose Canada Smile?
        </h2>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <div v-for="(feature, index) in features" :key="feature.title"
               class="text-center p-8 rounded-2xl border border-transparent hover:border-gold-800 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/20"
               :style="{animationDelay: `${index * 100}ms`}"
               :class="{'animate-fade-in-up': featuresVisible}">
            <div class="w-20 h-20 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-6 transform transition-transform duration-500 hover:rotate-12 hover:scale-110">
              <div class="transform transition-transform duration-500 hover:scale-110">
                <component :is="feature.icon" class="w-10 h-10 text-black-900" />
              </div>
            </div>
            <h3 class="text-2xl font-display font-bold text-gold-400 mb-4 transform transition-all duration-500 hover:scale-105">
              {{ feature.title }}
            </h3>
            <p class="text-gray-300 transform transition-all duration-500 hover:translate-y-1">
              {{ feature.description }}
              <!-- Special link for Digital Workflow -->
              <span v-if="feature.title === 'Digital Workflow'" class="block mt-2">
                <a href="#" @click.prevent="downloadPPTX" 
                   class="!outline-none !ring-0 text-gold-400 hover:text-gold-300 hover:no-underline transition-all duration-300 inline-flex items-center group cursor-pointer">
                  Download our Digital Workflow Guide (PPTX)
                </a>
              </span>
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Preview -->
    <section class="py-20 overflow-hidden">
      <div class="container mx-auto px-6">
        <h2 class="section-title transform transition-all duration-1000"
            :class="{'translate-y-0 opacity-100': servicesVisible, 'translate-y-10 opacity-0': !servicesVisible}"
            ref="servicesTitle">
          Our Services
        </h2>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
          <!-- Pick-Up & Delivery -->
          <div class="bg-black-800 rounded-2xl p-8 border border-gold-800 transform transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/30 hover:border-gold-500"
               :class="{'animate-slide-in-left': servicesVisible}">
            <div class="flex items-center mb-6">
              <h3 class="text-2xl font-display font-bold text-gold-400 transform transition-all duration-500 hover:translate-x-2">
                Pick-Up & Delivery
              </h3>
            </div>
            <p class="text-gray-300 text-lg mb-4 transform transition-all duration-500 hover:translate-y-1">
              Fast pick-up and delivery across the GTA. For other areas, we provide complimentary UPS & ICS shipping.
            </p>
            <div class="space-y-3">
              <div v-for="(point, index) in pickupPoints" :key="index"
                   class="flex items-center text-gray-300 transform transition-all duration-300 hover:translate-x-2"
                   :style="{transitionDelay: `${index * 100}ms`}">
                <svg class="w-5 h-5 text-gold-400 mr-3 flex-shrink-0 transform transition-transform duration-300 hover:scale-125">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span class="transform transition-all duration-300 hover:text-gold-300">{{ point }}</span>
              </div>
            </div>
          </div>
          
          <!-- Custom Shading & Staining -->
          <div class="bg-black-800 rounded-2xl p-8 border border-gold-800 transform transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/30 hover:border-gold-500"
               :class="{'animate-slide-in-up': servicesVisible}">
            <div class="flex items-center mb-6">
              <h3 class="text-2xl font-display font-bold text-gold-400 transform transition-all duration-500 hover:translate-x-2">
                Custom Shading & Staining
              </h3>
            </div>
            <p class="text-gray-300 text-lg mb-4 transform transition-all duration-500 hover:translate-y-1">
              Accurate shade matching with full-spectrum lighting.
            </p>
            <div class="space-y-3">
              <div v-for="(point, index) in shadingPoints" :key="index"
                   class="flex items-center text-gray-300 transform transition-all duration-300 hover:translate-x-2"
                   :style="{transitionDelay: `${index * 100}ms`}">
                <svg class="w-5 h-5 text-gold-400 mr-3 flex-shrink-0 transform transition-transform duration-300 hover:scale-125">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span class="transform transition-all duration-300 hover:text-gold-300">{{ point }}</span>
              </div>
            </div>
          </div>
          
          <!-- Digital Printing -->
          <div class="bg-black-800 rounded-2xl p-8 border border-gold-800 transform transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/30 hover:border-gold-500"
               :class="{'animate-slide-in-right': servicesVisible}">
            <div class="flex items-center mb-6">
              <h3 class="text-2xl font-display font-bold text-gold-400 transform transition-all duration-500 hover:translate-x-2">
                DIGITAL PRINTING
              </h3>
            </div>
            <p class="text-gray-300 text-lg mb-4 transform transition-all duration-500 hover:translate-y-1">
              We accept all digital impressions and intra-oral scans directly into our lab.
            </p>
            <div class="space-y-3">
              <div v-for="(point, index) in digitalPrintingPoints" :key="index"
                   class="flex items-center text-gray-300 transform transition-all duration-300 hover:translate-x-2"
                   :style="{transitionDelay: `${index * 100}ms`}">
                <svg class="w-5 h-5 text-gold-400 mr-3 flex-shrink-0 transform transition-transform duration-300 hover:scale-125">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                <span class="transform transition-all duration-300 hover:text-gold-300">{{ point }}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-center mt-12 transform transition-all duration-1000"
             :class="{'translate-y-0 opacity-100': servicesVisible, 'translate-y-10 opacity-0': !servicesVisible}">
          <router-link to="/services" class="btn-primary transform transition-all duration-500 hover:scale-110 hover:shadow-xl hover:shadow-gold-500/20">
            View All Services
          </router-link>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-20 bg-gradient-dark overflow-hidden">
      <div class="container mx-auto px-6 text-center">
        <div class="bg-black-900/30 rounded-3xl p-12 max-w-4xl mx-auto border border-gold-800 transform transition-all duration-1000 hover:scale-105 hover:shadow-2xl hover:shadow-gold-900/30"
             :class="{'animate-pulse-glow': ctaVisible}">
          <h2 class="text-4xl md:text-5xl font-display font-bold text-gold-400 mb-6 transform transition-all duration-700 hover:scale-105">
            Ready for Premium Dental Restorations?
          </h2>
          <p class="text-xl text-gold-300 mb-10 max-w-2xl mx-auto transform transition-all duration-700 hover:translate-y-1">
            Experience fast turnaround, premium materials, and natural aesthetic results with Canada Smile Lab.
          </p>
          <div class="flex flex-col sm:flex-row gap-6 justify-center">
            <a href="tel:+14167279598" class="btn-primary text-lg px-12 transform transition-all duration-500 hover:scale-110 hover:shadow-xl hover:shadow-gold-500/20">
              Call Now: (416) 727-9598
            </a>
            <router-link to="/contact" class="btn-secondary text-lg px-12 transform transition-all duration-500 hover:scale-110 hover:shadow-xl hover:shadow-gold-500/20">
              Contact Us
            </router-link>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import HeroSection from '@/components/HeroSection.vue'
import { 
  ClockIcon, 
  ChatBubbleLeftRightIcon, 
  CubeIcon,
  FaceSmileIcon,
  ComputerDesktopIcon,
  MapIcon
} from '@heroicons/vue/24/outline'

export default {
  name: 'Home',
  components: {
    HeroSection
  },
  data() {
    return {
      featuresVisible: false,
      servicesVisible: false,
      ctaVisible: false,
      features: [
        {
          icon: 'ClockIcon',
          title: '3-Day Turnaround',
          description: 'Fast delivery without compromising quality'
        },
        {
          icon: 'ChatBubbleLeftRightIcon',
          title: 'Reliable Communication',
          description: 'Clear and consistent updates throughout the process'
        },
        {
          icon: 'CubeIcon',
          title: 'Premium Materials',
          description: 'Only the highest quality materials for lasting results'
        },
        {
          icon: 'FaceSmileIcon',
          title: 'Natural, Aesthetic Results',
          description: 'Beautiful, lifelike restorations that blend seamlessly'
        },
        {
          icon: 'ComputerDesktopIcon',
          title: 'Digital Workflow',
          description: 'Better consistency and precision with advanced technology'
        },
        {
          icon: 'MapIcon',
          title: 'Nationwide Support',
          description: 'Supporting clinics across all of Canada'
        }
      ],
      pickupPoints: [
        'Fast pick-up and delivery across the GTA',
        'Complimentary UPS & ICS shipping for other areas',
        'Dentists across North America can receive our services via UPS, Purolator, and ICS'
      ],
      shadingPoints: [
        'Accurate shade matching with full-spectrum lighting',
        'Patients can visit our lab for final touch-ups before cementation'
      ],
      digitalPrintingPoints: [
        'Accept all digital impressions & intra-oral scans',
        'Process, design and print on-site',
        'Use digital models for high quality restorations',
        'Direct integration with our lab system'
      ]
    }
  },
  mounted() {
    // Import heroicons components dynamically
    const icons = { 
      ClockIcon, 
      ChatBubbleLeftRightIcon, 
      CubeIcon,
      FaceSmileIcon,
      ComputerDesktopIcon,
      MapIcon
    }
    this.features = this.features.map(feature => ({
      ...feature,
      icon: icons[feature.icon]
    }))

    // Intersection Observer for scroll animations
    this.setupIntersectionObserver()
    
    // Add custom CSS animations
    this.addCustomAnimations()
  },
  methods: {
    setupIntersectionObserver() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            if (entry.target === this.$refs.featuresTitle) {
              this.featuresVisible = true
            } else if (entry.target === this.$refs.servicesTitle) {
              this.servicesVisible = true
              setTimeout(() => {
                this.ctaVisible = true
              }, 500)
            }
          }
        })
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      })

      if (this.$refs.featuresTitle) observer.observe(this.$refs.featuresTitle)
      if (this.$refs.servicesTitle) observer.observe(this.$refs.servicesTitle)
    },
    addCustomAnimations() {
      const style = document.createElement('style')
      style.textContent = `
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes pulseGlow {
          0%, 100% {
            box-shadow: 0 0 20px rgba(230, 184, 0, 0.1);
          }
          50% {
            box-shadow: 0 0 40px rgba(230, 184, 0, 0.2);
          }
        }

        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-5px);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-right {
          animation: slideInRight 0.8s ease-out forwards;
          opacity: 0;
        }
        
        .animate-pulse-glow {
          animation: pulseGlow 3s ease-in-out infinite;
        }

        .animate-bounce {
          animation: bounce 0.5s ease-in-out infinite;
        }
        
        /* Hover effects for text */
        .hover-text-glow:hover {
          text-shadow: 0 0 10px rgba(230, 184, 0, 0.5);
        }
        
        /* Smooth transitions */
        * {
          scroll-behavior: smooth;
        }
        
        /* Gradient border animation */
        .gradient-border {
          position: relative;
          border: double 2px transparent;
          background-image: linear-gradient(black-800, black-800), 
                            linear-gradient(135deg, #e6b800 0%, #ffcf1a 100%);
          background-origin: border-box;
          background-clip: content-box, border-box;
          animation: borderRotate 3s linear infinite;
        }
        
        @keyframes borderRotate {
          0% {
            background-image: linear-gradient(black-800, black-800), 
                              linear-gradient(135deg, #e6b800 0%, #ffcf1a 100%);
          }
          50% {
            background-image: linear-gradient(black-800, black-800), 
                              linear-gradient(315deg, #e6b800 0%, #ffcf1a 100%);
          }
          100% {
            background-image: linear-gradient(black-800, black-800), 
                              linear-gradient(135deg, #e6b800 0%, #ffcf1a 100%);
          }
        }
      `
      document.head.appendChild(style)
    },
    downloadPPTX() {
      try {
        // Assuming your PPTX file is in the public folder
        // Update the filename if different
        const pptxFilename = 'WorkFlowCanadaSmile.pptx' // Change this to your actual filename
        
        // Create a temporary anchor element
        const link = document.createElement('a')
        link.href = `/${pptxFilename}`
        link.download = pptxFilename
        link.target = '_blank' // Open in new tab if download fails
        
        // Trigger download
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        // Optional: Add download confirmation
        this.showDownloadToast()
      } catch (error) {
        console.error('Error downloading file:', error)
        // Fallback: open in new tab
        window.open('/digital-workflow-guide.pptx', '_blank')
      }
    },
    showDownloadToast() {
      // Create a toast notification
      const toast = document.createElement('div')
      toast.className = 'fixed bottom-4 right-4 bg-gradient-to-r from-gold-600 to-gold-800 text-gray-900 px-6 py-3 rounded-lg shadow-lg z-50 animate-slide-in-right'
      toast.innerHTML = `
        <div class="flex items-center">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Download started!
        </div>
      `
      
      document.body.appendChild(toast)
      
      // Remove toast after 3 seconds
      setTimeout(() => {
        toast.classList.add('opacity-0', 'transition-opacity', 'duration-300')
        setTimeout(() => {
          if (toast.parentNode) {
            document.body.removeChild(toast)
          }
        }, 300)
      }, 3000)
    }
  },
  beforeUnmount() {
    // Clean up
    const style = document.querySelector('style[data-animations]')
    if (style) style.remove()
  }
}
</script>

<style scoped>
/* Additional hover effects */
.hover-lift {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.hover-lift:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

/* Gold shine effect */
.gold-shine {
  position: relative;
  overflow: hidden;
}

.gold-shine::after {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    to bottom right,
    rgba(230, 184, 0, 0) 0%,
    rgba(230, 184, 0, 0.1) 50%,
    rgba(230, 184, 0, 0) 100%
  );
  transform: rotate(30deg);
  transition: transform 0.6s ease;
}

.gold-shine:hover::after {
  transform: rotate(30deg) translate(20%, 20%);
}

/* Text gradient animation */
.animated-gradient {
  background: linear-gradient(135deg, #e6b800, #ffcf1a, #e6b800);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  animation: gradientShift 3s ease infinite;
}

@keyframes gradientShift {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Floating animation for decorative elements */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

.floating {
  animation: float 6s ease-in-out infinite;
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(50px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-in-up {
  animation: slideInUp 0.8s ease-out forwards;
  opacity: 0;
}

/* Download link styles */
.download-link {
  position: relative;
  overflow: hidden;
}

.download-link::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.download-link:hover::before {
  left: 100%;
}
</style>